const LOCALE_ALIASES = {
  "zh-CN": ["zh-CN", "zh-Hans", "zh_Hans", "hans", "zh"],
  "zh-TW": ["zh-TW", "zh-Hant", "zh_Hant", "hant", "tw"],
  en: ["en", "en-US", "en_US"],
  ja: ["ja", "ja-JP", "ja_JP"],
  vi: ["vi", "vi-VN", "vi_VN"],
  th: ["th", "th-TH", "th_TH"],
  ms: ["ms", "ms-MY", "ms_MY"],
};

export function normalizeLowCodeAssetLocale(locale) {
  const rawLocale = String(locale || "").trim();
  if (!rawLocale) return "";

  const normalized = rawLocale.replace(/_/g, "-");
  if (/^zh-(tw|hk|mo|hant)/i.test(normalized) || /^(hant|tw)$/i.test(normalized)) {
    return "zh-TW";
  }
  if (/^zh/i.test(normalized) || /^hans$/i.test(normalized)) {
    return "zh-CN";
  }
  if (/^en/i.test(normalized)) return "en";
  if (/^ja/i.test(normalized)) return "ja";
  if (/^vi/i.test(normalized)) return "vi";
  if (/^th/i.test(normalized)) return "th";
  if (/^ms/i.test(normalized)) return "ms";
  return rawLocale;
}

export function getLowCodeAssetLocaleCandidates(locale) {
  const rawLocale = String(locale || "").trim();
  const normalizedLocale = normalizeLowCodeAssetLocale(rawLocale);
  const candidates = [
    rawLocale,
    rawLocale.replace(/_/g, "-"),
    rawLocale.replace(/-/g, "_"),
    normalizedLocale,
    ...(LOCALE_ALIASES[normalizedLocale] || []),
  ];
  return [...new Set(candidates.filter(Boolean))];
}

function normalizeAssetValue(value) {
  if (typeof value === "string") {
    return value ? { url: value } : null;
  }
  if (value && typeof value === "object" && typeof value.url === "string" && value.url) {
    return value;
  }
  return null;
}

function resolveAssetFromMap(i18nAssets, fieldName, locale) {
  const fieldAssets = i18nAssets?.[fieldName];
  if (!fieldAssets || typeof fieldAssets !== "object") return null;
  const candidates = getLowCodeAssetLocaleCandidates(locale);
  for (const candidate of candidates) {
    const asset = normalizeAssetValue(fieldAssets[candidate]);
    if (asset) return asset;
  }
  return null;
}

export function resolveLocalizedAsset(owner, fieldName, locale) {
  const responsiveContext = owner?.__responsiveAssetContext;
  if (responsiveContext) {
    const deviceAsset = resolveAssetFromMap(
      responsiveContext.deviceI18nAssets,
      fieldName,
      locale,
    );
    if (deviceAsset) return deviceAsset;

    if (responsiveContext.deviceDefaultFields?.[fieldName]) {
      return normalizeAssetValue(owner?.[fieldName]) || { url: "" };
    }

    const baseAsset = resolveAssetFromMap(
      responsiveContext.baseI18nAssets,
      fieldName,
      locale,
    );
    if (baseAsset) return baseAsset;
  }

  const localizedAsset = resolveAssetFromMap(
    owner?.i18nAssets,
    fieldName,
    locale,
  );
  if (localizedAsset) return localizedAsset;

  const fallbackAsset = normalizeAssetValue(owner?.[fieldName]);
  return fallbackAsset || { url: "" };
}

export function resolveLocalizedAssetUrl(owner, fieldName, locale) {
  return resolveLocalizedAsset(owner, fieldName, locale).url || "";
}

export function localizeAssetFields(owner, fieldNames, locale) {
  if (!owner || typeof owner !== "object") return owner;
  let localizedOwner = owner;
  fieldNames.forEach((fieldName) => {
    const localizedUrl = resolveLocalizedAssetUrl(owner, fieldName, locale);
    if (localizedUrl !== (owner[fieldName] || "")) {
      if (localizedOwner === owner) localizedOwner = { ...owner };
      localizedOwner[fieldName] = localizedUrl;
    }
  });
  return localizedOwner;
}

export const LOW_CODE_IMAGE_FIELDS = [
  "backgroundImage", "src", "hoverSrc", "poster", "url", "activeUrl",
  "imgURL", "titleImage", "otherIcon", "btnIcon", "leftIcon", "rightIcon",
  "shareImage", "buttonBgImage", "icon", "linkIcon",
];

const IMAGE_COLLECTION_FIELDS = [
  "imageConfigs", "categoryConfigs", "standbyCategoryConfigs",
  "leftIconConfigs", "rightIconConfigs", "navigationList", "rightNavigationList",
];

// Localize image records, leaving child layouts and responsive overrides for
// their own renderer to resolve against the active breakpoint.
export function localizeLowCodeConfigAssets(owner, locale) {
  if (!owner || typeof owner !== "object") return owner;
  const fields = [...new Set([
    ...LOW_CODE_IMAGE_FIELDS.filter((fieldName) => typeof owner[fieldName] === "string"),
    ...Object.keys(owner.i18nAssets || {}),
    ...Object.keys(owner.__responsiveAssetContext?.baseI18nAssets || {}),
    ...Object.keys(owner.__responsiveAssetContext?.deviceI18nAssets || {}),
  ])];
  let localizedOwner = localizeAssetFields(owner, fields, locale);
  IMAGE_COLLECTION_FIELDS.forEach((fieldName) => {
    const records = owner[fieldName];
    if (!Array.isArray(records)) return;
    const localizedRecords = records.map((record) =>
      localizeLowCodeConfigAssets(record, locale),
    );
    if (localizedRecords.some((record, index) => record !== records[index])) {
      if (localizedOwner === owner) localizedOwner = { ...owner };
      localizedOwner[fieldName] = localizedRecords;
    }
  });
  return localizedOwner;
}
